export const termsAndConditionsContent = `
# FLOWNECT – TERMS & CONDITIONS

**Last Updated:** December 11, 2025

These Terms and Conditions (“Terms”) constitute a legally binding agreement between **Flownect**, a software service provider (“Flownect,” “Company,” “we,” “us,” or “our”), and any individual or entity that accesses or uses the Flownect platform (“User,” “you,” or “your”).

By accessing, registering for, downloading, or using the Flownect platform—including its mobile applications, web applications, dashboards, tools, APIs, and related services (collectively, the **“Platform”**)—you confirm that you have read, understood, and agree to be legally bound by these Terms.

These Terms incorporate the **Privacy Policy** by reference. The Privacy Policy forms an integral and binding part of this Agreement, and acceptance of these Terms constitutes acceptance of the Privacy Policy.

If you do not agree, you must immediately discontinue use of the Platform.

---

## 1. PURPOSE AND NATURE OF THE PLATFORM

Flownect is a software-as-a-service (SaaS) platform designed to provide operational, administrative, monitoring, communication, and data management tools to businesses operating in the cleaning industry and related services.

The Platform enables businesses to:
- Manage internal teams and subcontractors  
- Monitor job-site activity and attendance  
- Collect operational metrics  
- Store documents and compliance-related records  
- Communicate with workers through digital tools  

Flownect does not perform, supervise, guarantee, or control any cleaning services or job outcomes.  
All services performed in the real world are conducted independently by users outside of Flownect’s control.

---

## 2. USER CATEGORIES AND ROLES

The Platform may be accessed by different types of users, including but not limited to:

### 2.1 Individual / Contractor Profile
For workers, subcontractors, or people seeking opportunities in the cleaning industry.  
Individuals can showcase skills, availability, documents, and preferred work locations.

### 2.2 Business Profile
For companies seeking to manage their teams, contractors, operations, communications, and compliance through Flownect.

Premium features may include expanded analytics, GPS logs, advanced communication tools, and additional visibility of worker information.

Flownect does not determine, verify, or enforce how Business Users classify workers.  
Employment status, contractor classification, and compliance obligations are determined solely by the Business User.

---

## 3. NO EMPLOYMENT, AGENCY, OR CONTROL RELATIONSHIP

Flownect is not an employer, joint employer, co-employer, staffing agency, labor provider, agent, broker, or partner of any user.

Nothing in these Terms shall be interpreted as creating:
- An employment relationship  
- An agency relationship  
- A partnership or joint venture  

Flownect does not:
- Hire, fire, discipline, or supervise workers  
- Set wages, schedules, or work conditions  
- Control how services are performed  

All managerial authority rests exclusively with the Business User.

---

## 4. USER ACCOUNTS, INFORMATION, AND ACCESS

Users must provide accurate, complete, and current information when creating an account.

Users are solely responsible for:
- Safeguarding login credentials  
- All activity occurring under their account  
- Ensuring authorized access only  

Flownect has no obligation to monitor user activity but reserves the right to suspend, restrict, or terminate accounts if misuse, fraud, security risks, or violations are detected.

Use of the mobile application is also subject to any applicable terms, policies, and guidelines imposed by third-party app distribution platforms, including but not limited to the **Apple App Store** and **Google Play Store**.  
Flownect does not control and is not responsible for the enforcement or modification of such third-party policies.

---

## 5. SUBSCRIPTIONS, BILLING, AND PAYMENTS

Certain features require paid subscriptions.

By subscribing, you authorize Flownect, through third-party processors such as **Stripe**, **PayPal**, or any authorized processor, to charge recurring fees.

Flownect does not store full payment credentials and is not responsible for third-party processor errors.

### 5.1 Subscription Plans
Flownect offers free and paid plans, each with different features.

### 5.2 Automatic Renewal
Subscriptions renew automatically unless canceled before the next billing cycle.

### 5.3 Non-Refundable Fees
All subscription fees are non-refundable unless explicitly stated in the Refund Policy.

### 5.4 Refund Policy
Refunds may be considered for:
- Major technical platform failures  
- Duplicate or unintended charges  
- System errors  

Refund requests must be made within **30 days** of purchase and may require evidence.  
The maximum refund amount is limited to **one (1) month of service fees**.

---

## 6. LOCATION TRACKING, MONITORING, AND CONSENT

### 6.1 Scope of Location Data Collection
The Platform collects location-related data, which may include:
- Real-time GPS location  
- Background location tracking  
- Geofencing data (arrival and departure from job sites)  
- Historical location logs  

### 6.2 Purpose of Tracking
Location data is used exclusively for:
- Verifying attendance and presence at job sites  
- Generating internal operational metrics  
- Enabling Business Users to supervise field operations  

### 6.3 Mandatory Nature of Tracking
Certain Platform features cannot function without location tracking.  
By using those features, users expressly consent to continuous tracking.

### 6.4 Visibility and Responsibility
Location data may be visible to:
- Business account owners  
- Authorized managers or supervisors  

Flownect does not control how Business Users interpret or act upon location data and assumes no responsibility for employer decisions based on such data.

---

## 7. USER REPRESENTATIONS, WARRANTIES, AND CONSENTS

You represent and warrant that:
- You have legal authority to use the Platform  
- You have obtained all required consents from workers or contractors  
- You have provided legally required notices related to tracking and monitoring  
- All information submitted is accurate and lawful  

You acknowledge that Flownect relies on these representations and does not independently verify compliance.

---

## 8. LEGAL, LABOR, TAX, AND PRIVACY COMPLIANCE

Business Users are solely responsible for:
- Wage and hour compliance  
- Worker classification  
- Payroll, tax withholding, and reporting  
- Employment law compliance  
- Providing legally required privacy notices  

Flownect does not provide legal, tax, payroll, or HR advice.

---

## 9. UNACCEPTABLE USE AND PLATFORM CONDUCT

Prohibited behaviors include, but are not limited to:
- Using the Platform for unlawful purposes  
- Abusing surveillance or tracking features  
- Submitting misleading or fraudulent data  
- Attempting to reverse-engineer, hack, or disrupt the Platform  
- Uploading harmful or malicious content  

Violations may result in immediate suspension or termination.

Users may delete their accounts permanently. Upon deletion, data may be removed in accordance with the Privacy Policy.

---

## 10. DATA OWNERSHIP AND INTELLECTUAL PROPERTY

All Platform software, interfaces, and trademarks belong exclusively to Flownect.

Users retain ownership of their uploaded content but grant Flownect a limited license to host and process such content solely for Platform operation.

---

## 11. USER RESPONSIBILITIES

### 11.1 Business Responsibility
Businesses are solely responsible for:
- Conducting background checks  
- Verifying employment eligibility  
- Ensuring compliance with labor laws  
- Managing their workers and contractors  

### 11.2 Tax Obligations
Each user is responsible for:
- Filing and paying their own taxes  
- Providing accurate tax documentation (EIN, SSN, ITIN)  

Flownect does not collect or remit taxes on behalf of users.

---

## 12. INTERACTIONS & DISPUTES BETWEEN USERS

Flownect acts only as a software platform and is not a party to any agreements between:
- Businesses and workers  
- Contractors and subcontractors  

Flownect is not responsible for:
- Service disputes  
- Payment disputes  
- Job performance  
- Damages or losses  

Users release Flownect from any liability arising from user-to-user interactions.

---

## 13. INDEMNIFICATION

Users agree to indemnify and hold Flownect harmless from any claims, damages, penalties, investigations, or lawsuits arising from:
- Employment disputes  
- Misuse of tracking data  
- Failure to comply with labor, tax, or privacy laws  
- Claims brought by workers or regulators  

This obligation survives termination.

---

## 14. DISCLAIMER OF WARRANTIES

The Platform is provided **“AS IS”** and **“AS AVAILABLE.”**  
Flownect disclaims all warranties, including fitness for a particular purpose or uninterrupted availability.

---

## 15. MODIFICATIONS

Flownect may update these Terms at any time without prior notice.  
Continued use of the Platform constitutes acceptance of such changes.

---

## 16. LIMITATION OF LIABILITY

Flownect shall not be liable for:
- Lost profits  
- Employment claims  
- Regulatory penalties  
- Indirect or consequential damages  

Maximum liability is limited to fees paid in the preceding **twelve (12) months**.

---

## 17. TERMINATION AND EFFECT

Flownect may terminate access at any time.  
Termination does not release users from legal obligations accrued prior to termination.

---

## 18. FORCE MAJEURE

Flownect is not responsible for failures caused by events beyond reasonable control.

---

## 19. GOVERNING LAW AND VENUE

These Terms are governed by the laws of the **State of California**.  
Exclusive venue shall be California state courts.

Users may access the Platform from locations outside the United States. Such access does not alter the applicability of these Terms. Users accessing the Platform from outside the United States do so at their own initiative and are solely responsible for compliance with all applicable local, national, labor, tax, and data protection laws in their respective jurisdictions.

---

## 20. SEVERABILITY AND WAIVER

If any provision is invalid, remaining provisions remain enforceable.  
Failure to enforce a right is not a waiver.

---

## 21. SURVIVAL

Sections related to liability, indemnification, data, and dispute resolution survive termination.

---

## 22. ENTIRE AGREEMENT

These Terms represent the complete agreement between the parties.

---

## 23. CONTACT

**Email:** contact@flownect.com  
Premium users may receive priority support channels.
`;
